import { getPermissions, hasPermission } from "../../../utilis/permissionStorage";
import { getApiErrorMessage } from "../../../utilis/apiErrorMessage";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  TASK_PAGE_SHELL_CLASS,
  MODULE_PAGE_SHELL_STYLE,
} from "../../TaskManagement/workspaceConstants";
import { getIntakes } from "../../Services/apiServices";
import {
  fetchApplicationFullDetails,
  fetchClientAllNotes,
  fetchClientFinanceNotes,
  fetchCommissionInquiryFees,
  fetchCommissionSummary,
  fetchNewCommissionPlan,
  listCommissionInvoices,
  updateNewCommissionPlan,
  updateNewCommissionBonus,
  updateCommissionInstallment,
} from "./commissionsApi";
import {
  mapFinanceNotesToHistoryEntries,
  mapClientNotesToHistoryEntries,
  mapInvoiceNotesToHistoryEntries,
  collectInvoicePaymentDescriptions,
  mapPaymentDescriptionsToHistoryEntries,
} from "../../../utilis/invoiceEditHelpers";
import {
  getNotesForCategory,
  resolveNoteTabKey,
} from "../../../utilis/clientRecategorize";
import NotesCategoryMoveToolbar, {
  NoteCategorySelectCheckbox,
} from "../../Clients/NotesCategoryMoveToolbar";
import "../../Clients/noteCategoryMove.css";
import {
  hasCommissionDetailParams,
  cleanCommissionDetailReturnTo,
} from "./commissionDetailUtils";
import CommissionDetailHeader from "./detail/CommissionDetailHeader";
import CommissionDetailProfileSidebar from "./detail/CommissionDetailProfileSidebar";
import CommissionDetailFinancialSection from "./detail/CommissionDetailFinancialSection";
import CommissionInstallmentsCard from "./detail/CommissionInstallmentsCard";
import BonusInstallmentsCard from "./detail/BonusInstallmentsCard";
import CommissionInvoicesPanel from "./detail/CommissionInvoicesPanel";
import CommissionReconciliationPanel from "./detail/CommissionReconciliationPanel";
import CommissionDetailSkeleton from "./detail/CommissionDetailSkeleton";
import TuitionFeeSyncBanner from "./detail/TuitionFeeSyncBanner";
import ClientDocumentsModal from "./ClientDocumentsModal";
import {
  countClientDocuments,
  fetchCommissionApplicationDocuments,
} from "./clientDocumentsUtils";
import { apiUrl } from "../../ApiManager/AxiosHeaders";
import { mapCommissionDetailViewModel } from "./detail/commissionDetailMapper";
import {
  normalizeSavedCommissionBonus,
  normalizeSavedCommissionPlan,
  resolvePlanTuitionInstallmentBudget,
} from "./detail/commissionPlanUtils";
import { resolveUninvoicedInquiryFeeFlags } from "./detail/inquiryFeeInvoiceLedger";
import { formatCurrency } from "./detail/commissionDetailConstants";
import {
  analyzeBudgetAllocation,
  buildSyncedBonusPayload,
  buildSyncedCommissionPlanPayload,
  splitInstallmentsByLock,
  sumBonusInstallmentAmount,
  sumInstallmentTuition,
  tuitionDiffers,
} from "./detail/tuitionFeeSyncUtils";
import { applyPendingInstallmentTuitionRebalance } from "./detail/installmentTuitionRebalanceUtils";
import {
  installmentHasRemainingToInvoice,
  bonusInstallmentHasRemainingToInvoice,
} from "./commissionInvoiceLineSuggestions";
import Swal from "sweetalert2";
import { FaChevronDown, FaChevronUp, FaPlus } from "react-icons/fa";
import ClientAddNoteModal from "../../Clients/ClientAddNoteModal";
import Modals from "../../GenericComponents/Modal";
import { view_ApiManager } from "../../ApiManager/ApiManager";
import {
  fetchInvoiceFollowupsByInvoices,
  getInvoiceFollowupErrorMessage,
  updateInvoiceFollowup,
} from "../../../utilis/invoiceFollowupApi";
import {
  mapInvoiceFollowupsToHistoryEntries,
} from "../../../utilis/invoiceFollowupHelpers";
import {
  CONTEXT_INVOICE_COMMISSION,
  hasInvoiceFollowupPermission,
} from "../../../utilis/followupConstants";
import "../../../styles/tailwind.css";
import "./commissions-responsive.css";
import "../../Clients/clients.css";

function normalizeFullDetailsPayload(data) {
  if (!data || typeof data !== "object") return data;
  if (
    data.student ||
    data.client ||
    data.application ||
    data.commissions ||
    data.provider
  ) {
    return data;
  }
  if (data.details && typeof data.details === "object") return data.details;
  if (data.record && typeof data.record === "object") return data.record;
  return data;
}

function DetailSection({ title, subtitle, tone = "blue", children, action }) {
  const bar =
    tone === "amber"
      ? "bg-amber-500"
      : tone === "slate"
        ? "bg-slate-400"
        : "bg-[#007BFF]";
  const badge =
    tone === "amber"
      ? "border-amber-200 bg-amber-50 text-amber-700"
      : tone === "slate"
        ? "border-slate-200 bg-slate-50 text-slate-600"
        : "border-[#007BFF]/20 bg-[#007BFF]/8 text-[#007BFF]";

  return (
    <section className="space-y-2">
      <div className="flex w-full items-center gap-2.5">
        <span className={`h-4 w-1 shrink-0 rounded-full ${bar}`} aria-hidden />
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="mb-0 text-[13px] font-semibold tracking-tight text-slate-900">
              {title}
            </h2>
            {subtitle ? (
              <span
                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${badge}`}
              >
                {subtitle}
              </span>
            ) : null}
          </div>
        </div>
        <div className="hidden h-px flex-1 bg-slate-100 sm:block" aria-hidden />
        {action ? <div className="ml-auto shrink-0">{action}</div> : null}
      </div>
      {children}
    </section>
  );
}

function pickSummaryValue(source, keys = []) {
  if (!source || typeof source !== "object") return null;
  for (const key of keys) {
    if (source[key] != null && source[key] !== "") return source[key];
  }
  return null;
}

function formatSummaryMoney(value, currency = "AUD") {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  try {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: currency || "AUD",
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return String(num);
  }
}

function CommissionPlanSummaryStrip({ summary, currency = "AUD" }) {
  if (!summary || typeof summary !== "object") return null;

  const tuition = summary.tuition || summary.Tuition || {};
  const commission = summary.commission || summary.Commission || {};
  const bonus = summary.bonus || summary.Bonus || {};

  const tuitionPaid = pickSummaryValue(tuition, [
    "student_paid",
    "paid",
    "paid_amount",
  ]);
  const tuitionTotal = pickSummaryValue(tuition, [
    "total",
    "expected",
    "amount",
  ]);
  const commissionExpected = pickSummaryValue(commission, [
    "expected",
    "total",
    "amount",
  ]);
  const commissionInvoiced = pickSummaryValue(commission, [
    "invoiced",
    "invoiced_amount",
  ]);
  const commissionPaid = pickSummaryValue(commission, [
    "paid",
    "received",
    "paid_amount",
  ]);
  const bonusExpected = pickSummaryValue(bonus, [
    "expected",
    "total",
    "amount",
  ]);
  const bonusPaid = pickSummaryValue(bonus, [
    "paid",
    "received",
    "paid_amount",
  ]);

  const hasAny =
    tuitionPaid != null ||
    tuitionTotal != null ||
    commissionExpected != null ||
    commissionInvoiced != null ||
    commissionPaid != null ||
    bonusExpected != null ||
    bonusPaid != null ||
    summary.invoiced_amount != null ||
    summary.total_invoiced != null;

  if (!hasAny) return null;

  const items = [
    {
      label: "Tuition paid (student → provider)",
      value: formatSummaryMoney(tuitionPaid ?? tuition?.student_paid, currency),
      hint:
        tuitionTotal != null
          ? `of ${formatSummaryMoney(tuitionTotal, currency)}`
          : null,
    },
    {
      label: "Commission expected",
      value: formatSummaryMoney(
        commissionExpected ?? summary.expected_commission,
        currency,
      ),
    },
    {
      label: "Commission invoiced",
      value: formatSummaryMoney(
        commissionInvoiced ?? summary.invoiced_amount ?? summary.total_invoiced,
        currency,
      ),
    },
    {
      label: "Commission received",
      value: formatSummaryMoney(commissionPaid, currency),
    },
    {
      label: "Bonus",
      value:
        bonusExpected != null || bonusPaid != null
          ? `${formatSummaryMoney(bonusPaid ?? 0, currency)} / ${formatSummaryMoney(
              bonusExpected ?? 0,
              currency,
            )}`
          : null,
    },
  ].filter((item) => item.value != null && item.value !== "—");

  if (!items.length) return null;

  return (
    <div className="grid gap-2 rounded-lg border border-slate-200 bg-white p-2.5 shadow-sm sm:grid-cols-2 lg:grid-cols-5">
      {items.map((item) => (
        <div key={item.label} className="min-w-0 px-1.5 py-1">
          <p className="mb-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-400">
            {item.label}
          </p>
          <p className="mb-0 text-[13px] font-semibold tabular-nums text-slate-900">
            {item.value}
          </p>
          {item.hint ? (
            <p className="mb-0 text-[10px] text-slate-500">{item.hint}</p>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function formatNoteActionLabel(action) {
  if (!action) return "Activity";
  const map = {
    created: "Invoice created",
    payment_added: "Payment received",
    payment_reversed: "Payment reversed",
    updated: "Invoice updated",
    cancelled: "Invoice cancelled",
    credit_applied: "Credit applied",
    refunded: "Refund issued",
    followup_assigned: "Follow-up assigned",
    followup_completed: "Follow-up completed",
    followup_rescheduled: "Follow-up rescheduled",
    followup_reassigned: "Follow-up reassigned",
  };
  return map[action] || String(action).replace(/_/g, " ");
}

function formatNoteDate(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}

function noteActionTone(action) {
  switch (String(action || "").toLowerCase()) {
    case "followup_assigned":
    case "followup_completed":
    case "followup_rescheduled":
    case "followup_reassigned":
      return "bg-amber-100/80 text-amber-900 ring-amber-200/70";
    case "payment_added":
    case "payment":
      return "bg-emerald-50 text-emerald-700 ring-emerald-100";
    case "created":
    case "invoice created":
      return "bg-[#007BFF]/8 text-[#007BFF] ring-[#007BFF]/15";
    case "payment_reversed":
    case "cancelled":
      return "bg-rose-50 text-rose-700 ring-rose-100";
    case "refunded":
    case "credit_applied":
    case "finance":
      return "bg-amber-50 text-amber-700 ring-amber-100";
    default:
      return "bg-slate-50 text-slate-600 ring-slate-200";
  }
}

const NOTES_PREVIEW_LIMIT = 4;

function followupStatusTone(status) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "pending") {
    return "bg-amber-100 text-amber-900 ring-amber-200/80";
  }
  if (normalized === "completed") {
    return "bg-emerald-100 text-emerald-800 ring-emerald-200/80";
  }
  return "bg-slate-100 text-slate-700 ring-slate-200";
}

function formatFollowupStatusLabel(status) {
  const normalized = String(status || "").toLowerCase();
  if (!normalized) return "Unknown";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function NoteHistoryItem({
  entry,
  onCompleteFollowup,
  canCompleteFollowup,
  selectionPrefix = null,
}) {
  const isFollowup = Boolean(entry?.isFollowupActivity);
  const isFollowupRecord = Boolean(entry?.isFollowupRecord && entry?.followupId);
  const isClientNoteRow =
    !isFollowup && !isFollowupRecord && !entry?.invoiceNumber;
  const isPendingFollowup =
    isFollowupRecord && String(entry?.followupStatus || "").toLowerCase() === "pending";
  const showComplete =
    isPendingFollowup &&
    canCompleteFollowup &&
    typeof onCompleteFollowup === "function";

  return (
    <article
      className={`rounded-lg border px-3 py-2.5 ${
        isFollowup
          ? "border-amber-100/90 bg-amber-50/55"
          : "border-slate-100 bg-slate-50/70"
      }`}
    >
      <div className="flex gap-2">
        {selectionPrefix ? (
          <div className="shrink-0 pt-0.5">{selectionPrefix}</div>
        ) : null}
        <div className="min-w-0 flex-1">
      <div className="mb-1.5 flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${noteActionTone(
              entry.action,
            )}`}
          >
            {formatNoteActionLabel(entry.action)}
          </span>
          {isClientNoteRow && entry.isFinance ? (
            <span className="inline-flex items-center rounded-full bg-[#007BFF] px-2 py-0.5 text-[10px] font-semibold text-white">
              Finance
            </span>
          ) : null}
          {isFollowupRecord && entry.followupStatus ? (
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ring-1 ring-inset ${followupStatusTone(
                entry.followupStatus,
              )}`}
            >
              {formatFollowupStatusLabel(entry.followupStatus)}
            </span>
          ) : null}
          {entry.invoiceNumber ? (
            <span className="text-[10px] font-medium text-slate-500">
              Inv. {entry.invoiceNumber}
            </span>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <time className="whitespace-nowrap text-[11px] tabular-nums text-slate-400">
            {formatNoteDate(entry.date)}
          </time>
          {showComplete ? (
            <button
              type="button"
              className="rounded-md bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm transition hover:bg-emerald-700"
              style={{ border: "none" }}
              onClick={() => onCompleteFollowup(entry.followupId)}
            >
              Complete
            </button>
          ) : null}
        </div>
      </div>
      <p className="mb-1 text-[12px] font-semibold text-[#007BFF]">
        {entry.user || "—"}
      </p>
      {isFollowup && entry.followupAssignee ? (
        <p className="mb-1 text-[11px] font-medium text-amber-900/75">
          Assigned to {entry.followupAssignee}
        </p>
      ) : null}
      <p className="mb-0 whitespace-pre-line text-[12px] leading-relaxed text-slate-700">
        {entry.detail || "—"}
      </p>
      {isFollowupRecord && entry.result ? (
        <p className="mb-0 mt-1.5 text-[11px] text-slate-600">
          <span className="font-semibold">Result:</span> {entry.result}
        </p>
      ) : null}
      {isFollowup && entry.followupDate ? (
        <p className="mb-0 mt-1.5 text-[11px] text-slate-500">
          Follow-up date:{" "}
          <span className="font-semibold text-slate-700">
            {entry.followupDate}
          </span>
        </p>
      ) : null}
      {isClientNoteRow && entry.shownToClient ? (
        <p className="mb-0 mt-1.5 text-[11px] font-semibold text-amber-900/80">
          (This note shown to client)
        </p>
      ) : null}
        </div>
      </div>
    </article>
  );
}

function NotesHistoryListBody({
  entries = [],
  loading = false,
  emptyText = "No notes yet.",
  expanded = false,
  onToggleExpand,
  previewLimit = NOTES_PREVIEW_LIMIT,
  onCompleteFollowup = null,
  canCompleteFollowup = false,
  noteSelectionEnabled = false,
  selectedNoteIds = [],
  onToggleNoteSelection = null,
}) {
  if (loading) {
    return (
      <div className="flex min-h-[120px] items-center justify-center px-4 py-8">
        <p className="mb-0 text-[12px] font-medium text-slate-400">Loading…</p>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="flex min-h-[120px] flex-col items-center justify-center gap-1 px-4 py-8 text-center">
        <p className="mb-0 text-[13px] font-medium text-slate-500">{emptyText}</p>
        <p className="mb-0 text-[11px] text-slate-400">
          Activity will appear here when notes are added.
        </p>
      </div>
    );
  }

  const visibleEntries = expanded
    ? entries
    : entries.slice(0, previewLimit);
  const hasMore = entries.length > previewLimit;
  const hiddenCount = entries.length - previewLimit;

  return (
    <div className="px-3 py-3">
      <div className="space-y-2">
        {visibleEntries.map((entry) => {
          const selectableId =
            entry.noteId != null ? String(entry.noteId) : null;
          const showSelect =
            noteSelectionEnabled &&
            selectableId &&
            typeof onToggleNoteSelection === "function";
          return (
            <NoteHistoryItem
              key={entry.id}
              entry={entry}
              onCompleteFollowup={onCompleteFollowup}
              canCompleteFollowup={canCompleteFollowup}
              selectionPrefix={
                showSelect ? (
                  <NoteCategorySelectCheckbox
                    noteId={selectableId}
                    selected={selectedNoteIds.includes(selectableId)}
                    onToggle={onToggleNoteSelection}
                  />
                ) : null
              }
            />
          );
        })}
      </div>

      {hasMore ? (
        <button
          type="button"
          onClick={onToggleExpand}
          className="mt-2 flex w-full items-center justify-center gap-1 rounded-lg py-1.5 text-[11px] font-semibold text-[#007BFF] transition hover:bg-[#007BFF]/5"
          style={{ border: "none", boxShadow: "none" }}
        >
          {expanded ? (
            <>
              Show less
              <FaChevronUp size={9} aria-hidden />
            </>
          ) : (
            <>
              Show all ({entries.length})
              <FaChevronDown size={9} aria-hidden />
            </>
          )}
          {!expanded && hiddenCount > 0 ? (
            <span className="sr-only">{hiddenCount} more hidden</span>
          ) : null}
        </button>
      ) : null}
    </div>
  );
}

function UnifiedNotesHistoryCard({
  invoiceEntries = [],
  generalEntries = [],
  paymentEntries = [],
  financeEntries = [],
  loadingGeneral = false,
  loadingFinance = false,
  onAddNote,
  followupUsers = [],
  canCompleteFollowup = false,
  onFollowupUpdated,
  showFinancialTab = true,
  activeTab: controlledActiveTab,
  onActiveTabChange,
  notesToolbar = null,
  noteSelectionEnabled = false,
  selectedNoteIds = [],
  onToggleNoteSelection = null,
}) {
  const [internalActiveTab, setInternalActiveTab] = useState("general");
  const activeTab = controlledActiveTab ?? internalActiveTab;
  const setActiveTab = onActiveTabChange ?? setInternalActiveTab;

  useEffect(() => {
    if (!showFinancialTab && activeTab === "financial") {
      setActiveTab("general");
    }
  }, [showFinancialTab, activeTab, setActiveTab]);
  const [expandedByTab, setExpandedByTab] = useState({
    general: false,
    invoice: false,
    payments: false,
    financial: false,
  });
  const [resolveFollowupId, setResolveFollowupId] = useState(null);
  const [showFollowupCompleteModal, setShowFollowupCompleteModal] =
    useState(false);

  const handleCompleteFollowupRequest = useCallback((followupId) => {
    setResolveFollowupId(followupId);
    setShowFollowupCompleteModal(true);
  }, []);

  const handleFollowupCompleteCallback = async (
    _show,
    type,
    note,
    notetype,
    rescheduleDate,
    reassignId,
  ) => {
    if (type !== "edit") {
      setShowFollowupCompleteModal(false);
      setResolveFollowupId(null);
      return;
    }
    if (!resolveFollowupId) {
      setShowFollowupCompleteModal(false);
      return;
    }

    const resultText = String(note || "").trim();
    if (!resultText) {
      Swal.fire({
        icon: "warning",
        text: "Please enter a note before completing the follow-up.",
      });
      return;
    }

    const wantsReschedule =
      rescheduleDate !== false &&
      rescheduleDate !== "" &&
      rescheduleDate != null &&
      Boolean(String(rescheduleDate).trim());
    if (wantsReschedule && !String(rescheduleDate).trim()) {
      Swal.fire({
        icon: "warning",
        text: "Please select a reschedule date.",
      });
      return;
    }

    const reassignNumeric = parseInt(reassignId, 10);
    const wantsReassign =
      reassignId !== false &&
      reassignId !== "" &&
      reassignId != null &&
      Number.isFinite(reassignNumeric) &&
      reassignNumeric > 0;

    try {
      const data = await updateInvoiceFollowup(resolveFollowupId, {
        result: resultText,
        reschedule: wantsReschedule,
        rescheduleDate: wantsReschedule ? String(rescheduleDate).trim() : undefined,
        reassign: wantsReassign,
        reAssignID: wantsReassign ? reassignNumeric : undefined,
        noteType: notetype,
      });
      if (data?.error) {
        Swal.fire({ icon: "error", text: data.error });
        return;
      }
      setShowFollowupCompleteModal(false);
      setResolveFollowupId(null);
      Swal.fire({
        icon: "success",
        title: data?.message || "Follow-up updated",
        timer: 1500,
        showConfirmButton: false,
      });
      onFollowupUpdated?.();
    } catch (err) {
      Swal.fire({
        icon: "error",
        text: getInvoiceFollowupErrorMessage(err, err?.response?.data),
      });
    }
  };

  const generalCount = generalEntries.length;
  const invoiceCount = invoiceEntries.length;
  const paymentCount = paymentEntries.length;
  const financeCount = showFinancialTab ? financeEntries.length : 0;
  const totalCount =
    generalCount +
    invoiceCount +
    paymentCount +
    (showFinancialTab ? financeCount : 0);

  const tabs = [
    {
      id: "general",
      label: "General notes",
      count: generalCount,
      emptyText: "No general notes yet.",
      loading: loadingGeneral,
      entries: generalEntries,
    },
    {
      id: "invoice",
      label: "Invoice notes & activity",
      count: invoiceCount,
      emptyText: "No invoice notes yet.",
      loading: false,
      entries: invoiceEntries,
    },
    {
      id: "payments",
      label: "Payment descriptions",
      count: paymentCount,
      emptyText: "No payment descriptions yet.",
      loading: false,
      entries: paymentEntries,
    },
    ...(showFinancialTab
      ? [
          {
            id: "financial",
            label: "Financial notes",
            count: financeCount,
            emptyText: "No financial notes yet.",
            loading: loadingFinance,
            entries: financeEntries,
          },
        ]
      : []),
  ];

  const active =
    tabs.find((tab) => tab.id === activeTab) || tabs[0];

  return (
    <DetailSection
      title="Notes & activity"
      subtitle={`${totalCount} ${totalCount === 1 ? "entry" : "entries"}`}
      tone="slate"
      action={
        onAddNote ? (
          <button
            type="button"
            onClick={onAddNote}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#007BFF] px-3 py-1.5 text-[12px] font-semibold text-white shadow-sm transition hover:bg-[#0069d9]"
            style={{ border: "none" }}
          >
            <FaPlus size={10} aria-hidden />
            Add note
          </button>
        ) : null
      }
    >
      <div className="overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <div className="flex flex-wrap items-center gap-1 border-b border-slate-100 bg-slate-50/80 px-2 py-1.5">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-semibold transition ${
                  isActive
                    ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200"
                    : "text-slate-500 hover:bg-white/70 hover:text-slate-700"
                }`}
                style={{ border: "none" }}
              >
                {tab.label}
                <span
                  className={`inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${
                    isActive
                      ? "bg-slate-100 text-slate-600"
                      : "bg-slate-200/70 text-slate-500"
                  }`}
                >
                  {tab.loading ? "…" : tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {notesToolbar ? (
          <div className="border-b border-slate-100 px-2 py-2">{notesToolbar}</div>
        ) : null}

        <NotesHistoryListBody
          entries={active.entries}
          loading={active.loading}
          emptyText={active.emptyText}
          expanded={expandedByTab[activeTab]}
          onToggleExpand={() =>
            setExpandedByTab((prev) => ({
              ...prev,
              [activeTab]: !prev[activeTab],
            }))
          }
          onCompleteFollowup={
            activeTab === "invoice" ? handleCompleteFollowupRequest : null
          }
          canCompleteFollowup={
            activeTab === "invoice" ? canCompleteFollowup : false
          }
          noteSelectionEnabled={
            noteSelectionEnabled &&
            (activeTab === "general" || activeTab === "financial")
          }
          selectedNoteIds={selectedNoteIds}
          onToggleNoteSelection={onToggleNoteSelection}
        />
      </div>

      <Modals
        show={showFollowupCompleteModal}
        title="Add Completed Note"
        data="Add Note"
        type="category"
        reschedule={true}
        textarea="true"
        reassign={true}
        invoiceFollowupComplete={true}
        allUsers={followupUsers}
        assignedTo={resolveFollowupId}
        parentCallback={handleFollowupCompleteCallback}
      />
    </DetailSection>
  );
}

function extractNotesFromInvoiceRow(row) {
  if (!row || typeof row !== "object") return [];
  if (Array.isArray(row.notes) && row.notes.length) return row.notes;
  if (Array.isArray(row.raw?.notes) && row.raw.notes.length)
    return row.raw.notes;
  if (Array.isArray(row.wrapper?.notes) && row.wrapper.notes.length) {
    return row.wrapper.notes;
  }
  if (Array.isArray(row.invoice_notes) && row.invoice_notes.length) {
    return row.invoice_notes;
  }
  return [];
}

function collectInvoiceNotes(invoiceRows = []) {
  const collected = [];
  (Array.isArray(invoiceRows) ? invoiceRows : []).forEach((row) => {
    const invNumber = row?.invoice_number || row?.invoiceNumber || null;
    const invId = row?.id || row?.financeInvoiceId || null;
    extractNotesFromInvoiceRow(row).forEach((note) => {
      if (!note || typeof note !== "object") return;
      collected.push({
        ...note,
        invoice_id: note.invoice_id ?? invId,
        invoice_number: note.invoice_number || invNumber,
      });
    });
  });
  collected.sort((a, b) => {
    const ta = new Date(a.action_date || a.created_at || 0).getTime();
    const tb = new Date(b.action_date || b.created_at || 0).getTime();
    return tb - ta;
  });
  return collected;
}

const DETAIL_WORKSPACE_TABS = [
  { id: "installments", label: "Installments" },
  { id: "bonuses", label: "Bonuses" },
  { id: "invoices", label: "Invoices" },
];

function CommissionDetailPage({ params, missingParams = false }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(!missingParams);
  const [error, setError] = useState(
    missingParams ? "Missing required parameters." : null,
  );
  const [details, setDetails] = useState(null);
  const [intakes, setIntakes] = useState([]);
  const [savedPlan, setSavedPlan] = useState(null);
  const [savedBonuses, setSavedBonuses] = useState([]);
  const [savedInstallments, setSavedInstallments] = useState([]);
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [commissionInvoices, setCommissionInvoices] = useState([]);
  const [planLoading, setPlanLoading] = useState(false);
  const [planSummary, setPlanSummary] = useState(null);
  const [invoiceRefreshKey, setInvoiceRefreshKey] = useState(0);
  const [planFullyLoaded, setPlanFullyLoaded] = useState(false);
  const [syncingTuition, setSyncingTuition] = useState(false);
  const [inquiryFeeRecord, setInquiryFeeRecord] = useState(null);
  const [invoiceNotes, setInvoiceNotes] = useState([]);
  const [invoiceFollowups, setInvoiceFollowups] = useState([]);
  const [followupUsers, setFollowupUsers] = useState([]);
  const [invoiceFollowupRefreshKey, setInvoiceFollowupRefreshKey] = useState(0);
  const [invoicePayments, setInvoicePayments] = useState([]);
  const [allClientNotes, setAllClientNotes] = useState([]);
  const [financeNotes, setFinanceNotes] = useState([]);
  const [loadingGeneralNotes, setLoadingGeneralNotes] = useState(false);
  const [loadingFinanceNotes, setLoadingFinanceNotes] = useState(false);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [notesActiveTab, setNotesActiveTab] = useState("general");
  const [selectedGeneralNoteIds, setSelectedGeneralNoteIds] = useState([]);
  const [selectedFinanceNoteIds, setSelectedFinanceNoteIds] = useState([]);
  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
  const [clientDocuments, setClientDocuments] = useState([]);
  const [workspaceTab, setWorkspaceTab] = useState(() => {
    const tab = searchParams.get("tab");
    if (tab === "invoices" || tab === "bonuses" || tab === "installments") {
      return tab;
    }
    return "installments";
  });

  const planIdFromUrl =
    searchParams.get("planId") || searchParams.get("plan_id") || null;
  const invoiceStatusTabFromUrl = searchParams.get("invoiceStatusTab");
  const invoicesOnlyReturn = Boolean(searchParams.get("refreshInvoices"));
  const lastInvoiceListRefreshTokenRef = useRef("");

  // After create/edit invoice → open Invoices tab and reload invoice list.
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "invoices" || tab === "bonuses" || tab === "installments") {
      setWorkspaceTab(tab);
    }
    const refreshToken = searchParams.get("refreshInvoices");
    if (refreshToken && planIdFromUrl) {
      setSavedPlan((prev) =>
        prev?.id && String(prev.id) === String(planIdFromUrl)
          ? prev
          : { id: Number(planIdFromUrl) || planIdFromUrl, currency: "AUD" },
      );
      if (refreshToken !== lastInvoiceListRefreshTokenRef.current) {
        lastInvoiceListRefreshTokenRef.current = refreshToken;
        setWorkspaceTab("invoices");
        setInvoiceRefreshKey((key) => key + 1);
      }
    }
  }, [searchParams, planIdFromUrl]);

  const refreshClientNotes = useCallback(async (clientId) => {
    if (!clientId) {
      setAllClientNotes([]);
      setFinanceNotes([]);
      return;
    }

    const permissions = getPermissions() || {};
    const hasFinance =
      permissions.finance_notes === "1" || permissions.finance_notes === 1;

    setLoadingGeneralNotes(true);
    if (hasFinance) {
      setLoadingFinanceNotes(true);
    } else {
      setLoadingFinanceNotes(false);
      setFinanceNotes([]);
    }

    try {
      const generalRows = await fetchClientAllNotes(clientId);
      let financeRows = [];
      if (hasFinance) {
        financeRows = await fetchClientFinanceNotes(clientId);
      }
      setAllClientNotes(Array.isArray(generalRows) ? generalRows : []);
      setFinanceNotes(Array.isArray(financeRows) ? financeRows : []);
    } catch {
      setAllClientNotes([]);
      setFinanceNotes([]);
    } finally {
      setLoadingGeneralNotes(false);
      setLoadingFinanceNotes(false);
    }
  }, []);

  const loadSavedPlan = useCallback(
    async ({
      applicationCourseId,
      planId,
      skipSummary = false,
      commissionableTuitionFee,
    } = {}) => {
      if (missingParams || !hasCommissionDetailParams(params)) return null;

      setPlanLoading(true);
      try {
        const result = await fetchNewCommissionPlan({
          planId: planId || undefined,
          applicationId: params.appId,
          applicationCourseId: applicationCourseId || undefined,
          clientId: params.studentId,
          providerId: params.providerId,
          courseId: params.courseId,
        });
        const normalized = normalizeSavedCommissionPlan(result?.plan, {
          currency: "AUD",
          applicationCourseId,
          commissionableTuitionFee,
        });
        setSavedPlan(normalized);
        setSavedBonuses(
          (Array.isArray(result?.bonuses) ? result.bonuses : [])
            .map((row) => normalizeSavedCommissionBonus(row))
            .filter(Boolean),
        );
        setSavedInstallments(
          Array.isArray(result?.installments) ? result.installments : [],
        );
        setPlanFullyLoaded(true);

        if (normalized?.id) {
          try {
            const invoiceRows = await listCommissionInvoices(normalized.id);
            const invoiceList = Array.isArray(invoiceRows) ? invoiceRows : [];
            setInvoiceCount(invoiceList.length);
            setCommissionInvoices(invoiceList);
            setInvoiceNotes(collectInvoiceNotes(invoiceList));
            setInvoicePayments(collectInvoicePaymentDescriptions(invoiceList));
          } catch {
            setInvoiceCount(0);
            setCommissionInvoices([]);
            setInvoiceNotes([]);
            setInvoicePayments([]);
          }
        } else {
          setInvoiceCount(0);
          setCommissionInvoices([]);
          setInvoiceNotes([]);
          setInvoicePayments([]);
        }

        if (!skipSummary && normalized?.id) {
          const embeddedSummary = result?.summary ?? null;
          if (embeddedSummary) {
            setPlanSummary(embeddedSummary);
          } else {
            try {
              const summary = await fetchCommissionSummary(normalized.id);
              setPlanSummary(summary);
            } catch (summaryError) {
              console.error(summaryError);
              setPlanSummary(null);
            }
          }
        } else if (!normalized?.id) {
          setPlanSummary(null);
        }

        return normalized;
      } catch (err) {
        console.error(err);
        setSavedPlan(null);
        setSavedBonuses([]);
        setSavedInstallments([]);
        setInvoiceCount(0);
        setInvoiceNotes([]);
        setInvoicePayments([]);
        setPlanSummary(null);
        setPlanFullyLoaded(false);
        return null;
      } finally {
        setPlanLoading(false);
      }
    },
    [missingParams, params],
  );

  const loadDetails = useCallback(async () => {
    if (missingParams || !hasCommissionDetailParams(params)) return;

    setLoading(true);
    setError(null);
    try {
      const [data, intakesResult, feeRecord] = await Promise.all([
        fetchApplicationFullDetails({
          applicationId: params.appId,
          type: params.type,
          clientId: params.studentId,
          providerId: params.providerId,
          courseId: params.courseId,
          applicationCourseId: params.applicationCourseId,
        }),
        getIntakes().catch(() => ({ data: null })),
        fetchCommissionInquiryFees({
          applicationId: params.appId,
          clientId: params.studentId,
          providerId: params.providerId,
          applicationCourseId: params.applicationCourseId,
        }).catch(() => ({
          application_fee: 0,
          material_fee: 0,
          currency: "AUD",
        })),
      ]);

      const normalized = normalizeFullDetailsPayload(data);
      setDetails(normalized);
      setInquiryFeeRecord(feeRecord);
      const intakeList = intakesResult?.data?.data || intakesResult?.data || [];
      setIntakes(Array.isArray(intakeList) ? intakeList : []);

      const mapped = mapCommissionDetailViewModel(normalized, {
        intakes: Array.isArray(intakeList) ? intakeList : [],
        courseId: params?.courseId,
        applicationCourseId: params?.applicationCourseId,
      });
      await loadSavedPlan({
        applicationCourseId:
          params?.applicationCourseId ||
          mapped?.commissionPlan?.defaults?.applicationCourseId,
        commissionableTuitionFee:
          mapped?.commissionPlan?.defaults?.commissionableTuitionFee,
      });
    } catch (err) {
      setError(getApiErrorMessage(err) || err?.message || "Failed to load details.");
      setDetails(null);
      setSavedPlan(null);
      setSavedBonuses([]);
      setSavedInstallments([]);
      setInvoiceCount(0);
      setInvoiceNotes([]);
      setInvoicePayments([]);
      setPlanSummary(null);
    } finally {
      setLoading(false);
    }
  }, [params, missingParams, loadSavedPlan]);

  useEffect(() => {
    if (missingParams || !hasCommissionDetailParams(params)) return undefined;

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const [data, intakesResult, feeRecord] = await Promise.all([
          fetchApplicationFullDetails({
            applicationId: params.appId,
            type: params.type,
            clientId: params.studentId,
            providerId: params.providerId,
            courseId: params.courseId,
            applicationCourseId: params.applicationCourseId,
          }),
          getIntakes().catch(() => ({ data: null })),
          fetchCommissionInquiryFees({
            applicationId: params.appId,
            clientId: params.studentId,
            providerId: params.providerId,
            applicationCourseId: params.applicationCourseId,
          }).catch(() => ({
            application_fee: 0,
            material_fee: 0,
            currency: "AUD",
          })),
        ]);

        if (cancelled) return;

        const normalized = normalizeFullDetailsPayload(data);
        setDetails(normalized);
        setInquiryFeeRecord(feeRecord);
        const intakeList =
          intakesResult?.data?.data || intakesResult?.data || [];
        setIntakes(Array.isArray(intakeList) ? intakeList : []);

        const mapped = mapCommissionDetailViewModel(normalized, {
          intakes: Array.isArray(intakeList) ? intakeList : [],
          courseId: params?.courseId,
          applicationCourseId: params?.applicationCourseId,
        });

        if (!cancelled) {
          await loadSavedPlan({
            applicationCourseId:
              params?.applicationCourseId ||
              mapped?.commissionPlan?.defaults?.applicationCourseId,
            planId: planIdFromUrl || undefined,
            commissionableTuitionFee:
              mapped?.commissionPlan?.defaults?.commissionableTuitionFee,
          });
          if (invoicesOnlyReturn) {
            setWorkspaceTab("invoices");
            setInvoiceRefreshKey((key) => key + 1);
          }
        }
      } catch (err) {
        if (cancelled) return;
        setError(getApiErrorMessage(err) || err?.message || "Failed to load details.");
        setDetails(null);
        setSavedPlan(null);
        setSavedBonuses([]);
        setSavedInstallments([]);
        setInvoiceCount(0);
        setInvoiceNotes([]);
        setInvoicePayments([]);
        setPlanSummary(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [params, missingParams, loadSavedPlan, invoicesOnlyReturn, planIdFromUrl]);

  const viewModel = useMemo(
    () =>
      details
        ? mapCommissionDetailViewModel(details, {
            intakes,
            courseId: params?.courseId,
          })
        : null,
    [details, intakes, params?.courseId],
  );

  const commissionClientId = useMemo(
    () => params?.studentId || viewModel?.student?.id || null,
    [params?.studentId, viewModel?.student?.id],
  );

  useEffect(() => {
    if (!commissionClientId) {
      setAllClientNotes([]);
      setFinanceNotes([]);
      setLoadingGeneralNotes(false);
      setLoadingFinanceNotes(false);
      return undefined;
    }

    let cancelled = false;

    const run = async () => {
      await refreshClientNotes(commissionClientId);
      if (cancelled) return;
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [commissionClientId, refreshClientNotes]);

  const hasFinanceNotePermission = useMemo(() => {
    const permissions = getPermissions() || {};
    return (
      permissions.finance_notes === "1" || permissions.finance_notes === 1
    );
  }, []);

  const hasSettingsPageAccess = useMemo(() => {
    const permissions = getPermissions() || {};
    return permissions.settings_page === "1" || permissions.settings_page === 1;
  }, []);

  const commissionProviderId = useMemo(
    () => params?.providerId || viewModel?.provider?.id || null,
    [params?.providerId, viewModel?.provider?.id],
  );

  const commissionAppType = useMemo(() => {
    const raw = String(params?.type || "education").toLowerCase();
    if (raw === "education" || raw === "edu") return "edu";
    if (raw === "migration" || raw === "mig") return "mig";
    return raw || "edu";
  }, [params?.type]);

  const refreshClientDocuments = useCallback(
    async (nextDocs) => {
      if (Array.isArray(nextDocs)) {
        setClientDocuments(nextDocs);
        return nextDocs;
      }
      if (!commissionClientId) {
        setClientDocuments([]);
        return [];
      }
      const next = await fetchCommissionApplicationDocuments({
        clientId: commissionClientId,
        providerId: commissionProviderId,
        apiUrl,
      });
      setClientDocuments(next);
      return next;
    },
    [commissionClientId, commissionProviderId],
  );

  const clientDocumentCount = useMemo(
    () => countClientDocuments(clientDocuments),
    [clientDocuments],
  );

  const openDocumentsModal = useCallback(() => {
    setDocumentsModalOpen(true);
  }, []);

  useEffect(() => {
    if (!commissionClientId || !viewModel) return;
    refreshClientDocuments();
  }, [commissionClientId, commissionProviderId, viewModel, refreshClientDocuments]);

  const inquiryWithFees = useMemo(() => {
    if (!viewModel?.inquiry) return null;
    const applicationFee = inquiryFeeRecord?.application_fee;
    const materialFee = inquiryFeeRecord?.material_fee;
    return {
      ...viewModel.inquiry,
      applicationFee:
        applicationFee != null && Number(applicationFee) > 0
          ? String(applicationFee)
          : applicationFee != null && applicationFee !== ""
            ? String(applicationFee)
            : "",
      materialFee:
        materialFee != null && Number(materialFee) > 0
          ? String(materialFee)
          : materialFee != null && materialFee !== ""
            ? String(materialFee)
            : "",
      applicationFeeStatus: inquiryFeeRecord?.application_fee_status || "none",
      materialFeeStatus: inquiryFeeRecord?.material_fee_status || "none",
      applicationFeePaid: inquiryFeeRecord?.application_fee_paid ?? 0,
      materialFeePaid: inquiryFeeRecord?.material_fee_paid ?? 0,
    };
  }, [viewModel?.inquiry, inquiryFeeRecord]);

  const handleWorkspaceTabChange = useCallback(
    (tabId) => {
      setWorkspaceTab(tabId);
      // Lazy-load plan/summary only when leaving invoices-only return mode.
      if (
        !planFullyLoaded &&
        (tabId === "installments" || tabId === "bonuses") &&
        hasCommissionDetailParams(params)
      ) {
        loadSavedPlan({
          planId: savedPlan?.id || planIdFromUrl || undefined,
          applicationCourseId:
            viewModel?.commissionPlan?.defaults?.applicationCourseId,
          commissionableTuitionFee:
            viewModel?.commissionPlan?.defaults?.commissionableTuitionFee,
        });
      }
    },
    [
      planFullyLoaded,
      params,
      loadSavedPlan,
      savedPlan?.id,
      planIdFromUrl,
      viewModel,
    ],
  );

  const planContext = useMemo(() => {
    if (!viewModel?.commissionPlan) return null;
    return {
      ...viewModel.commissionPlan,
      existing: savedPlan || viewModel.commissionPlan.existing || null,
      bonuses: savedBonuses,
      installments: savedInstallments,
      summary: planSummary,
      loading: planLoading,
    };
  }, [
    viewModel,
    savedPlan,
    savedBonuses,
    savedInstallments,
    planSummary,
    planLoading,
  ]);

  const latestCommissionableTuitionFee = Number(
    viewModel?.commissionPlan?.defaults?.commissionableTuitionFee ??
      viewModel?.inquiry?.commissionable_tuition_fee ??
      0,
  );

  const planCommissionableTuitionFee = Number(
    savedPlan?.commissionableTuitionFee ??
      savedPlan?.commissionable_tuition_fee ??
      savedPlan?.tuitionFee ??
      savedPlan?.tuition_fee ??
      0,
  );

  const needsTuitionSync =
    Boolean(savedPlan?.id) &&
    Number.isFinite(latestCommissionableTuitionFee) &&
    tuitionDiffers(
      latestCommissionableTuitionFee,
      planCommissionableTuitionFee,
    );

  const runTuitionPlanSync = useCallback(
    async ({
      latestTuition = latestCommissionableTuitionFee,
      plan = savedPlan,
      bonus = savedBonuses[0] || null,
      installments = savedInstallments,
    } = {}) => {
      if (!plan?.id) return;
      const tuition = Number(latestTuition);
      if (!Number.isFinite(tuition) || tuition < 0) return;

      setSyncingTuition(true);
      try {
        const defaults = viewModel?.commissionPlan?.defaults || {};
        const planPayload = buildSyncedCommissionPlanPayload({
          plan,
          defaults,
          latestTuition: tuition,
          params,
          student: viewModel?.student,
          provider: viewModel?.provider,
          course: viewModel?.course,
          application: viewModel?.application,
          bonus,
        });
        await updateNewCommissionPlan(plan.id, planPayload);

        let nextExpectedBonus = Number(bonus?.expectedBonus) || 0;
        if (bonus?.id) {
          const syncedBonus = buildSyncedBonusPayload({
            bonus,
            latestTuition: tuition,
          });
          if (syncedBonus?.tuitionDriven) {
            await updateNewCommissionBonus(bonus.id, syncedBonus.payload);
            nextExpectedBonus = syncedBonus.expectedBonus;
          }
        }

        const syncedPlan = {
          ...plan,
          commissionableTuitionFee: tuition,
          commissionable_tuition_fee: tuition,
        };
        const installmentTuitionBudget = resolvePlanTuitionInstallmentBudget(
          syncedPlan,
          bonus,
        );

        let workingInstallments = installments;
        const rebalanceResult = await applyPendingInstallmentTuitionRebalance({
          installments: workingInstallments,
          newPlanCommissionable: installmentTuitionBudget,
          plan: syncedPlan,
          updateInstallment: updateCommissionInstallment,
        });
        workingInstallments = rebalanceResult.installments;

        const notes = [];
        if (rebalanceResult.applied > 0) {
          notes.push(
            `Adjusted ${rebalanceResult.applied} non-invoiced tuition installment(s) to match ${formatCurrency(tuition, plan.currency)} commissionable tuition.`,
          );
        }

        const commissionAlloc = analyzeBudgetAllocation(
          sumInstallmentTuition(workingInstallments),
          installmentTuitionBudget,
        );
        const bonusRows = Array.isArray(bonus?.installments)
          ? bonus.installments
          : [];
        const bonusAlloc = analyzeBudgetAllocation(
          sumBonusInstallmentAmount(bonusRows),
          nextExpectedBonus,
        );

        if (commissionAlloc.isUnder) {
          notes.push(
            `Tuition increased — add tuition fee installments for the remaining ${formatCurrency(commissionAlloc.remaining, plan.currency)}.`,
          );
          setWorkspaceTab("installments");
        }
        if (commissionAlloc.isOver) {
          const { pending, locked } = splitInstallmentsByLock(workingInstallments);
          if (locked.length > 0) {
            notes.push(
              `Tuition decreased. ${locked.length} installment(s) are invoiced (locked). Edit or delete pending installments to bring the total down to ${formatCurrency(tuition, plan.currency)}.`,
            );
          } else if (pending.length > 0) {
            notes.push(
              `Tuition decreased — installments are over by ${formatCurrency(commissionAlloc.overBy, plan.currency)}. Edit pending tuition fee installments to adjust amounts, or delete individual ones.`,
            );
          }
          setWorkspaceTab("installments");
        }

        if (bonus?.id && bonusAlloc.isUnder) {
          notes.push(
            `Bonus room left — add bonus installments for ${formatCurrency(bonusAlloc.remaining, plan.currency)}.`,
          );
        }
        if (bonus?.id && bonusAlloc.isOver) {
          const { pending, locked } = splitInstallmentsByLock(bonusRows);
          if (locked.length > 0) {
            notes.push(
              `${locked.length} bonus installment(s) are invoiced (locked). Edit or delete pending bonus installments to adjust.`,
            );
          } else if (pending.length > 0) {
            notes.push(
              `Bonus installments are over by ${formatCurrency(bonusAlloc.overBy, plan.currency)}. Edit pending bonus installments to adjust amounts, or delete individual ones.`,
            );
          }
          setWorkspaceTab("bonuses");
        }

        await loadSavedPlan({
          planId: plan.id,
          applicationCourseId: defaults.applicationCourseId,
          commissionableTuitionFee: defaults.commissionableTuitionFee,
        });
        setInvoiceRefreshKey((key) => key + 1);

        await Swal.fire({
          icon: "success",
          title: "Plan updated",
          html:
            notes.length > 0
              ? `<ul style="text-align:left;margin:0;padding-left:18px;font-size:13px;color:#475569">${notes
                  .map((n) => `<li style="margin-bottom:6px">${n}</li>`)
                  .join("")}</ul>`
              : "Commission plan now matches the latest tuition fee.",
          confirmButtonColor: "#007BFF",
        });
      } catch (error) {
        await Swal.fire(
          "Error",
          error?.message || "Failed to sync plan with new tuition.",
          "error",
        );
      } finally {
        setSyncingTuition(false);
      }
    },
    [
      latestCommissionableTuitionFee,
      savedPlan,
      savedBonuses,
      savedInstallments,
      viewModel,
      params,
      loadSavedPlan,
    ],
  );

  const handleInquiryFeesPaidSaved = useCallback(
    async (savedRecord) => {
      if (savedRecord) {
        setInquiryFeeRecord(savedRecord);
      }
      if (savedPlan?.id) {
        try {
          const summary = await fetchCommissionSummary(savedPlan.id);
          setPlanSummary(summary);
        } catch (summaryError) {
          console.error(summaryError);
        }
      }
    },
    [savedPlan?.id],
  );

  const handleInquirySaved = useCallback(
    async (meta = {}) => {
      if (missingParams || !hasCommissionDetailParams(params)) return;

      const previousPlanCommissionableTuition =
        Number(savedPlan?.commissionableTuitionFee) || 0;
      const planSnapshot = savedPlan;
      const bonusSnapshot = savedBonuses[0] || null;
      const installmentSnapshot = savedInstallments;
      const hadPlan = Boolean(planSnapshot?.id);

      await loadDetails();

      const nextCommissionableTuition = Number(meta.commissionable_tuition_fee);
      if (
        !hadPlan ||
        !Number.isFinite(nextCommissionableTuition) ||
        !tuitionDiffers(
          nextCommissionableTuition,
          previousPlanCommissionableTuition,
        )
      ) {
        return;
      }

      const currency = planSnapshot?.currency || "AUD";
      const confirm = await Swal.fire({
        icon: "question",
        title: "Commissionable tuition fee changed",
        html: `
        <p style="margin:0 0 8px;font-size:14px;color:#334155">
          Commissionable tuition changed from
          <strong>${formatCurrency(previousPlanCommissionableTuition, currency)}</strong>
          to
          <strong>${formatCurrency(nextCommissionableTuition, currency)}</strong>.
        </p>
        <p style="margin:0;font-size:13px;color:#64748b">
          Update the commission plan (and percentage bonus) to match?
          Non-invoiced installments will be adjusted automatically; invoiced rows are not changed.
        </p>
      `,
        showCancelButton: true,
        confirmButtonText: "Update plan",
        cancelButtonText: "Later",
        confirmButtonColor: "#007BFF",
        reverseButtons: true,
      });

      if (confirm.isConfirmed) {
        await runTuitionPlanSync({
          latestTuition: nextCommissionableTuition,
          plan: planSnapshot,
          bonus: bonusSnapshot,
          installments: installmentSnapshot,
        });
      }
    },
    [
      loadDetails,
      missingParams,
      params,
      savedPlan,
      savedBonuses,
      savedInstallments,
      runTuitionPlanSync,
    ],
  );

  const onPlanCreated = useCallback(
    async (createdPlanId) => {
      await loadSavedPlan({
        planId: createdPlanId || undefined,
        applicationCourseId:
          viewModel?.commissionPlan?.defaults?.applicationCourseId,
        commissionableTuitionFee:
          viewModel?.commissionPlan?.defaults?.commissionableTuitionFee,
      });
    },
    [loadSavedPlan, viewModel],
  );

  const onBonusSaved = useCallback(async () => {
    await loadSavedPlan({
      planId: savedPlan?.id || undefined,
      applicationCourseId:
        viewModel?.commissionPlan?.defaults?.applicationCourseId,
      commissionableTuitionFee:
        viewModel?.commissionPlan?.defaults?.commissionableTuitionFee,
    });
    setInvoiceRefreshKey((key) => key + 1);
  }, [loadSavedPlan, viewModel, savedPlan?.id]);

  const currency = savedPlan?.currency || viewModel?.kpi?.currency || "AUD";
  const hasBonusEnabled = Boolean(savedPlan?.hasBonus ?? savedBonuses[0]?.id);
  const bonusInstallments =
    hasBonusEnabled && Array.isArray(savedBonuses[0]?.installments)
      ? savedBonuses[0].installments
      : [];
  const hasUninvoicedInstallments =
    savedInstallments.some((row) => installmentHasRemainingToInvoice(row)) ||
    bonusInstallments.some((row) => bonusInstallmentHasRemainingToInvoice(row));
  const hasUninvoicedInquiryFees = useMemo(
    () => resolveUninvoicedInquiryFeeFlags(inquiryFeeRecord).any,
    [inquiryFeeRecord],
  );
  const canCreateInvoice =
    Boolean(savedPlan?.id) &&
    (hasUninvoicedInstallments || hasUninvoicedInquiryFees);

  const generalClientNotes = useMemo(
    () => getNotesForCategory(allClientNotes, financeNotes, "general"),
    [allClientNotes, financeNotes],
  );

  const financeClientNotes = useMemo(
    () => getNotesForCategory(allClientNotes, financeNotes, "financial"),
    [allClientNotes, financeNotes],
  );

  const toggleGeneralNoteSelection = useCallback((noteId) => {
    setSelectedGeneralNoteIds((prev) =>
      prev.includes(noteId)
        ? prev.filter((id) => id !== noteId)
        : [...prev, noteId],
    );
  }, []);

  const toggleFinanceNoteSelection = useCallback((noteId) => {
    setSelectedFinanceNoteIds((prev) =>
      prev.includes(noteId)
        ? prev.filter((id) => id !== noteId)
        : [...prev, noteId],
    );
  }, []);

  const refreshNotesAfterRecategorize = useCallback(
    async (targetCategory) => {
      const tabKey = resolveNoteTabKey(targetCategory);
      setNotesActiveTab(tabKey);
      setSelectedGeneralNoteIds([]);
      setSelectedFinanceNoteIds([]);
      await refreshClientNotes(commissionClientId);
    },
    [commissionClientId, refreshClientNotes],
  );

  const canCompleteCommissionFollowup = useMemo(() => {
    try {
      const permissions =
        JSON.parse(JSON.stringify(getPermissions()) || "{}") || {};
      return hasInvoiceFollowupPermission(
        CONTEXT_INVOICE_COMMISSION,
        permissions,
      );
    } catch {
      return false;
    }
  }, []);

  const invoiceHistoryEntries = useMemo(() => {
    const noteEntries = mapInvoiceNotesToHistoryEntries(invoiceNotes, {
      excludeFollowupAudit: true,
    });
    const followupEntries = mapInvoiceFollowupsToHistoryEntries(invoiceFollowups);
    return [...noteEntries, ...followupEntries].sort(
      (left, right) =>
        new Date(right.date).getTime() - new Date(left.date).getTime(),
    );
  }, [invoiceNotes, invoiceFollowups]);

  const loadInvoiceFollowupsForPlan = useCallback(async (invoiceRows = []) => {
    const ids = [
      ...new Set(
        (Array.isArray(invoiceRows) ? invoiceRows : [])
          .map(
            (row) =>
              row?.id ||
              row?.financeInvoiceId ||
              row?.finance_invoice_id ||
              null,
          )
          .filter((id) => id != null && String(id).trim())
          .map((id) => String(id)),
      ),
    ];

    if (!ids.length) {
      setInvoiceFollowups([]);
      return;
    }

    try {
      const rowsByInvoice = await fetchInvoiceFollowupsByInvoices(ids);
      const merged = ids.flatMap((id) => rowsByInvoice[id] || []).filter(Boolean);
      const byId = new Map();
      merged.forEach((row) => {
        if (row?.id != null) byId.set(String(row.id), row);
      });
      setInvoiceFollowups([...byId.values()]);
    } catch {
      setInvoiceFollowups([]);
    }
  }, []);

  useEffect(() => {
    view_ApiManager(
      "getAllUsers",
      0,
      0,
      (res) => setFollowupUsers(res?.data?.data || []),
      () => setFollowupUsers([]),
    );
  }, []);

  useEffect(() => {
    loadInvoiceFollowupsForPlan(commissionInvoices);
  }, [commissionInvoices, invoiceFollowupRefreshKey, loadInvoiceFollowupsForPlan]);

  const paymentHistoryEntries = useMemo(
    () => mapPaymentDescriptionsToHistoryEntries(invoicePayments),
    [invoicePayments],
  );
  const generalHistoryEntries = useMemo(
    () => mapClientNotesToHistoryEntries(generalClientNotes),
    [generalClientNotes],
  );
  const financeHistoryEntries = useMemo(
    () => mapFinanceNotesToHistoryEntries(financeClientNotes),
    [financeClientNotes],
  );

  const commissionNotesToolbar = useMemo(() => {
    if (!hasSettingsPageAccess) return null;
    if (notesActiveTab === "general") {
      return (
        <NotesCategoryMoveToolbar
          notes={generalClientNotes}
          selectedIds={selectedGeneralNoteIds}
          onToggleNote={toggleGeneralNoteSelection}
          onSelectAll={setSelectedGeneralNoteIds}
          onClearSelection={() => setSelectedGeneralNoteIds([])}
          clientId={commissionClientId}
          currentCategory="general"
          onSuccess={refreshNotesAfterRecategorize}
          setActiveNoteTab={setNotesActiveTab}
          enabled={hasSettingsPageAccess}
          embedded
        />
      );
    }
    if (notesActiveTab === "financial" && hasFinanceNotePermission) {
      return (
        <NotesCategoryMoveToolbar
          notes={financeClientNotes}
          selectedIds={selectedFinanceNoteIds}
          onToggleNote={toggleFinanceNoteSelection}
          onSelectAll={setSelectedFinanceNoteIds}
          onClearSelection={() => setSelectedFinanceNoteIds([])}
          clientId={commissionClientId}
          currentCategory="financial"
          onSuccess={refreshNotesAfterRecategorize}
          setActiveNoteTab={setNotesActiveTab}
          enabled={hasSettingsPageAccess}
          embedded
        />
      );
    }
    return null;
  }, [
    hasSettingsPageAccess,
    hasFinanceNotePermission,
    notesActiveTab,
    generalClientNotes,
    financeClientNotes,
    selectedGeneralNoteIds,
    selectedFinanceNoteIds,
    toggleGeneralNoteSelection,
    toggleFinanceNoteSelection,
    commissionClientId,
    refreshNotesAfterRecategorize,
  ]);

  const handleNoteAdded = useCallback(
    async ({ isFinanceNote } = {}) => {
      await refreshClientNotes(commissionClientId);
      if (isFinanceNote && hasFinanceNotePermission) {
        setNotesActiveTab("financial");
      } else if (!isFinanceNote) {
        setNotesActiveTab("general");
      }
    },
    [commissionClientId, refreshClientNotes, hasFinanceNotePermission],
  );

  const detailReturnTo = useMemo(() => {
    if (typeof window !== "undefined") {
      return `${window.location.pathname}${window.location.search}`;
    }
    return "/commissions/detail";
  }, [params]);

  const handleInvoiceFollowupUpdated = useCallback(async () => {
    setInvoiceFollowupRefreshKey((key) => key + 1);
    setInvoiceRefreshKey((key) => key + 1);
  }, []);

  const refreshAfterInvoiceChange = useCallback(async () => {
    await loadSavedPlan({
      planId: savedPlan?.id || planIdFromUrl || undefined,
      applicationCourseId:
        viewModel?.commissionPlan?.defaults?.applicationCourseId,
      commissionableTuitionFee:
        viewModel?.commissionPlan?.defaults?.commissionableTuitionFee,
    });
    setInvoiceRefreshKey((key) => key + 1);
    setInvoiceFollowupRefreshKey((key) => key + 1);
  }, [
    loadSavedPlan,
    savedPlan?.id,
    planIdFromUrl,
    viewModel?.commissionPlan?.defaults?.applicationCourseId,
    viewModel?.commissionPlan?.defaults?.commissionableTuitionFee,
  ]);

  const handleInvoicesLoaded = useCallback((invoiceRows = []) => {
    const invoiceList = Array.isArray(invoiceRows) ? invoiceRows : [];
    setInvoiceCount(invoiceList.length);
    setCommissionInvoices(invoiceList);
    setInvoiceNotes(collectInvoiceNotes(invoiceList));
    setInvoicePayments(collectInvoicePaymentDescriptions(invoiceList));

    // Sync installment paid / invoice links from invoice list payload
    // (avoids calling GET /new-commissions/{id} and /summary after save).
    const commissionUpdates = new Map();
    const bonusUpdates = new Map();
    const activeInvoiceIds = new Set();

    invoiceList.forEach((row) => {
      const invoiceId = row?.id || row?.financeInvoiceId || null;
      if (invoiceId != null) activeInvoiceIds.add(String(invoiceId));
      const invoiceNumber = row?.invoiceNumber || row?.invoice_number || null;
      (row?.linkedInstallments || row?.linked_installments || []).forEach(
        (inst) => {
          if (!inst?.id) return;
          commissionUpdates.set(String(inst.id), {
            status: inst.status || "paid",
            financeInvoiceId: invoiceId,
            invoiceId,
            invoiceNumber,
            commissionStatusLabel:
              inst.commission_status_label ||
              (inst.status === "paid" ? "Received 100%" : null),
            commissionReceivedPercent: Number(
              inst.commission_received_percent ??
                (inst.status === "paid" ? 100 : 0),
            ),
          });
        },
      );
      (
        row?.linkedBonusInstallments ||
        row?.linked_bonus_installments ||
        []
      ).forEach((inst) => {
        if (!inst?.id) return;
        bonusUpdates.set(String(inst.id), {
          status: inst.status || "paid",
          financeInvoiceId: invoiceId || null,
          invoiceId: invoiceId || null,
          invoiceNumber,
        });
      });
    });

    setSavedInstallments((prev) =>
      (prev || []).map((row) => {
        const patch = commissionUpdates.get(String(row.id));
        if (patch) {
          return {
            ...row,
            ...patch,
            commissionStatusLabel:
              patch.commissionStatusLabel === "received_100"
                ? "Received 100%"
                : patch.commissionStatusLabel || row.commissionStatusLabel,
          };
        }
        if (
          row.financeInvoiceId &&
          !activeInvoiceIds.has(String(row.financeInvoiceId))
        ) {
          return {
            ...row,
            status: "pending",
            financeInvoiceId: null,
            invoiceId: null,
            invoiceNumber: null,
            commissionStatusLabel: "Pending",
            commissionReceivedPercent: 0,
          };
        }
        return row;
      }),
    );

    setSavedBonuses((prev) =>
      (prev || []).map((bonus) => ({
        ...bonus,
        installments: (bonus.installments || []).map((row) => {
          const patch = bonusUpdates.get(String(row.id));
          if (patch) return { ...row, ...patch };
          if (
            row.financeInvoiceId &&
            !activeInvoiceIds.has(String(row.financeInvoiceId))
          ) {
            return {
              ...row,
              status: "pending",
              financeInvoiceId: null,
              invoiceId: null,
              invoiceNumber: null,
            };
          }
          return row;
        }),
      })),
    );
  }, []);

  const openCreateInvoice = useCallback(() => {
    const clientId = params.studentId || viewModel?.student?.id;
    if (!clientId || !canCreateInvoice) return;

    const cleanReturnTo = cleanCommissionDetailReturnTo(
      `${window.location.pathname}${window.location.search}`,
      { planId: savedPlan?.id },
    );
    const query = new URLSearchParams();
    if (savedPlan?.id) query.set("planId", String(savedPlan.id));
    if (params.appId || viewModel?.application?.id) {
      query.set("appId", String(params.appId || viewModel.application.id));
    }
    if (params.type) query.set("type", String(params.type));
    if (params.providerId || viewModel?.provider?.id) {
      query.set(
        "providerId",
        String(params.providerId || viewModel.provider.id),
      );
    }
    if (params.courseId || viewModel?.course?.id) {
      query.set("courseId", String(params.courseId || viewModel.course.id));
    }
    const applicationCourseId =
      params.applicationCourseId ||
      viewModel?.commissionPlan?.defaults?.applicationCourseId ||
      params.courseId ||
      viewModel?.course?.id ||
      "";
    if (applicationCourseId) {
      query.set("application_course_id", String(applicationCourseId));
    }

    const qs = query.toString();
    navigate(`/commissions/create-invoice/${clientId}${qs ? `?${qs}` : ""}`, {
      state: {
        returnTo: cleanReturnTo,
        planId: savedPlan?.id || null,
        applicationId: params.appId || viewModel?.application?.id,
        applicationType: params.type || "education",
        currency,
        courseName:
          viewModel?.course?.name ||
          viewModel?.inquiry?.course ||
          planContext?.defaults?.courseName ||
          "",
        providerName:
          viewModel?.provider?.name || viewModel?.inquiry?.providerName || "",
        provider: viewModel?.provider || null,
        studentName: viewModel?.student?.name || "",
        studentId:
          viewModel?.student?.studentId ||
          viewModel?.inquiry?.studentId ||
          "",
        studentDob:
          viewModel?.student?.birthDate ||
          viewModel?.inquiry?.studentBirthDate ||
          "",
        startDate:
          viewModel?.course?.startDate || viewModel?.inquiry?.start || "",
        endDate: viewModel?.course?.endDate || viewModel?.inquiry?.end || "",
        commissionRate: savedPlan?.commissionRate || "",
        commissionType: savedPlan?.commissionType || "percentage",
        providerId: params.providerId || viewModel?.provider?.id || "",
        courseId: params.courseId || viewModel?.course?.id || "",
        applicationCourseId,
        commissionInstallments: savedInstallments,
        bonusInstallments,
        inquiryFeeRecord,
      },
    });
  }, [
    params,
    viewModel,
    canCreateInvoice,
    savedPlan,
    currency,
    planContext,
    savedInstallments,
    bonusInstallments,
    navigate,
    inquiryFeeRecord,
  ]);

  return (
    <div className="tailwind-container">
      <div className={`${TASK_PAGE_SHELL_CLASS} cms-commissions-shell`} style={MODULE_PAGE_SHELL_STYLE}>
        {loading && <CommissionDetailSkeleton />}

        {!loading && error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {!loading && !error && !viewModel && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            No commission details were returned for this application.
          </div>
        )}

        {!loading && viewModel && (
          <div className="space-y-3">
            <CommissionDetailHeader header={viewModel.header} />

            <DetailSection title="Inquiry details" tone="slate">
              <CommissionDetailProfileSidebar
                inquiry={inquiryWithFees || viewModel.inquiry}
                student={viewModel.student}
                provider={viewModel.provider}
                course={viewModel.course}
                application={viewModel.application}
                params={params}
                currency={viewModel.kpi?.currency}
                onSaved={handleInquirySaved}
                documentCount={clientDocumentCount}
                onOpenDocuments={
                  commissionClientId ? openDocumentsModal : undefined
                }
              />
            </DetailSection>

            {needsTuitionSync ? (
              <TuitionFeeSyncBanner
                planCommissionableTuitionFee={planCommissionableTuitionFee}
                latestCommissionableTuitionFee={latestCommissionableTuitionFee}
                currency={currency}
                busy={syncingTuition}
                onSync={() =>
                  runTuitionPlanSync({
                    latestCommissionableTuitionFee:
                      latestCommissionableTuitionFee,
                  })
                }
              />
            ) : null}

            <CommissionReconciliationPanel
              applicationId={params.appId}
              applicationType="education"
              currency={currency}
            />

            <DetailSection
              title="Commission & payment overview"
              subtitle={
                savedPlan?.id
                  ? hasBonusEnabled && savedBonuses[0]?.id
                    ? "Commission + bonus + discount"
                    : hasBonusEnabled
                      ? "Commission + bonus setup"
                      : "Commission plan"
                  : "Setup required"
              }
              tone="blue"
            >
              <CommissionDetailFinancialSection
                planContext={planContext}
                params={params}
                student={viewModel.student}
                provider={viewModel.provider}
                course={viewModel.course}
                application={viewModel.application}
                currency={viewModel.kpi?.currency}
                hasBonusEnabled={hasBonusEnabled}
                inquiryFeeRecord={inquiryFeeRecord}
                commissionInvoices={commissionInvoices}
                inquiryFeesReadOnly
                onInquiryFeesPaidSaved={handleInquiryFeesPaidSaved}
                onPlanCreated={onPlanCreated}
                onBonusSaved={onBonusSaved}
              />
            </DetailSection>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <div
                className="client-invoices-xero__tabs flex flex-wrap gap-1 border-b border-slate-100 bg-slate-50/80 px-2 py-1.5"
                role="tablist"
              >
                {DETAIL_WORKSPACE_TABS.map((tab) => {
                  const isActive = workspaceTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      className={`client-invoices-xero__tab${
                        isActive ? " is-active" : ""
                      }`}
                      onClick={() => handleWorkspaceTabChange(tab.id)}
                    >
                      {tab.label}
                      {tab.id === "installments"
                        ? ` (${savedInstallments.length})`
                        : tab.id === "bonuses"
                          ? ` (${bonusInstallments.length})`
                          : tab.id === "invoices"
                            ? ` (${invoiceCount})`
                            : ""}
                    </button>
                  );
                })}
              </div>

              <div className="p-2.5">
                {workspaceTab === "installments" ? (
                  <CommissionInstallmentsCard
                    plan={savedPlan}
                    bonus={savedBonuses[0] || null}
                    installments={savedInstallments}
                    currency={currency}
                    clientId={params.studentId || viewModel.student?.id}
                    returnTo={detailReturnTo}
                    onSaved={onBonusSaved}
                  />
                ) : null}

                {workspaceTab === "bonuses" && hasBonusEnabled ? (
                  <BonusInstallmentsCard
                    plan={savedPlan}
                    bonus={savedBonuses[0] || null}
                    currency={currency}
                    clientId={params.studentId || viewModel.student?.id}
                    returnTo={detailReturnTo}
                    onSaved={onBonusSaved}
                  />
                ) : null}

                {workspaceTab === "invoices" ? (
                  <CommissionInvoicesPanel
                    planId={savedPlan?.id}
                    clientId={params.studentId || viewModel.student?.id}
                    returnTo={detailReturnTo}
                    currency={currency}
                    canCreateInvoice={canCreateInvoice}
                    onCreateInvoice={openCreateInvoice}
                    onChanged={refreshAfterInvoiceChange}
                    onInvoicesLoaded={handleInvoicesLoaded}
                    refreshKey={invoiceRefreshKey}
                    initialStatusTab={invoiceStatusTabFromUrl}
                    invoiceContext={{
                      applicationId: params.appId || viewModel?.application?.id,
                      applicationType: params.type || "education",
                      currency,
                      courseName:
                        viewModel?.course?.name ||
                        viewModel?.inquiry?.course ||
                        "",
                      providerName:
                        viewModel?.provider?.name ||
                        viewModel?.inquiry?.providerName ||
                        "",
                      studentName: viewModel?.student?.name || "",
                      studentId:
                        viewModel?.student?.studentId ||
                        viewModel?.inquiry?.studentId ||
                        "",
                      studentDob:
                        viewModel?.student?.birthDate ||
                        viewModel?.inquiry?.studentBirthDate ||
                        "",
                      startDate:
                        viewModel?.course?.startDate ||
                        viewModel?.inquiry?.start ||
                        "",
                      endDate:
                        viewModel?.course?.endDate ||
                        viewModel?.inquiry?.end ||
                        "",
                      commissionRate: savedPlan?.commissionRate || "",
                      commissionType: savedPlan?.commissionType || "percentage",
                      providerId:
                        params.providerId || viewModel?.provider?.id || "",
                      courseId: params.courseId || viewModel?.course?.id || "",
                      applicationCourseId:
                        params.applicationCourseId ||
                        viewModel?.commissionPlan?.defaults?.applicationCourseId ||
                        "",
                    }}
                  />
                ) : null}
              </div>
            </div>

            <UnifiedNotesHistoryCard
              generalEntries={generalHistoryEntries}
              invoiceEntries={invoiceHistoryEntries}
              paymentEntries={paymentHistoryEntries}
              financeEntries={financeHistoryEntries}
              followupUsers={followupUsers}
              canCompleteFollowup={canCompleteCommissionFollowup}
              onFollowupUpdated={handleInvoiceFollowupUpdated}
              loadingGeneral={loadingGeneralNotes}
              loadingFinance={loadingFinanceNotes}
              showFinancialTab={hasFinanceNotePermission}
              activeTab={notesActiveTab}
              onActiveTabChange={setNotesActiveTab}
              notesToolbar={commissionNotesToolbar}
              noteSelectionEnabled={hasSettingsPageAccess}
              selectedNoteIds={
                notesActiveTab === "financial"
                  ? selectedFinanceNoteIds
                  : selectedGeneralNoteIds
              }
              onToggleNoteSelection={
                notesActiveTab === "financial"
                  ? toggleFinanceNoteSelection
                  : toggleGeneralNoteSelection
              }
              onAddNote={() => setShowAddNoteModal(true)}
            />

            <ClientAddNoteModal
              show={showAddNoteModal}
              clientId={commissionClientId}
              onClose={() => setShowAddNoteModal(false)}
              onSuccess={handleNoteAdded}
            />

            <ClientDocumentsModal
              open={documentsModalOpen}
              clientName={
                inquiryWithFees?.clientName ||
                viewModel.inquiry?.clientName ||
                "Client"
              }
              documents={clientDocuments}
              clientId={commissionClientId}
              providerId={commissionProviderId}
              appId={params.appId}
              appType={commissionAppType}
              onDocumentsChange={refreshClientDocuments}
              onClose={() => setDocumentsModalOpen(false)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default CommissionDetailPage;
